import axios from 'axios'

import { Env, readFromEnv } from '@cinch-nx/environments'
import { datadogRum } from '@datadog/browser-rum'
import { formatPostcode } from '../utils/postcode-helper'

export type AddressIoSuggestion = {
  address: string
  url: string
  id: string
}

type AddressIoSuggestionsResponse = {
  suggestions: AddressIoSuggestion[]
  data: { suggestions: AddressIoSuggestion[] }
}

type AddressIoAddressDetails = {
  postcode: string
  latitude: number
  longitude: number
  formatted_address: string[]
  thoroughfare: string
  building_name: string
  sub_building_name: string
  sub_building_number: string
  building_number: string
  line_1: string
  line_2: string
  line_3: string
  line_4: string
  locality: string
  town_or_city: string
  county: string
  district: string
  country: string
  residential: boolean
}

const getAddressIoService = axios.create({
  baseURL: readFromEnv(Env.GetAddressIoUrl),
  params: {
    'api-key': readFromEnv(Env.GetAddressIoApiKey),
    all: true,
  },
})

export const findAddresses = async (postcode: string) => {
  try {
    const response =
      await getAddressIoService.request<AddressIoSuggestionsResponse>({
        method: 'GET',
        url: `/autocomplete/${formatPostcode(postcode)}`,
      })

    if (!response || !response.data || !response.data.suggestions) {
      datadogRum.addAction('API Success: Find Addresses - No Results', {
        apiEndpoint: '/autocomplete',
        postcode: formatPostcode(postcode),
        resultCount: 0,
      })
      return []
    }

    datadogRum.addAction('API Success: Find Addresses', {
      apiEndpoint: '/autocomplete',
      postcode: formatPostcode(postcode),
      resultCount: response.data.suggestions.length,
    })

    return response.data.suggestions
  } catch (error) {
    datadogRum.addError(new Error('API Failure: Find Addresses'), {
      apiEndpoint: '/autocomplete',
      postcode: formatPostcode(postcode),
      error: error,
    })
    throw error
  }
}

export const getAddressDetails = async (addressId: string) => {
  try {
    const response = await getAddressIoService.request<AddressIoAddressDetails>(
      {
        method: 'GET',
        url: `/get/${addressId}`,
      },
    )

    if (!response || !response.data) {
      datadogRum.addAction('API Success: Get Address Details - No Data', {
        apiEndpoint: '/get',
        addressId,
      })
      return null
    }

    datadogRum.addAction('API Success: Get Address Details', {
      apiEndpoint: '/get',
      addressId,
      hasData: true,
    })

    return {
      addressLine1: response.data.line_1,
      addressLine2: response.data.line_2,
      postTown: response.data.town_or_city,
      county: response.data.county,
      postcode: response.data.postcode,
    }
  } catch (error) {
    datadogRum.addError(new Error('API Failure: Get Address Details'), {
      apiEndpoint: '/get',
      addressId,
      error: error,
    })
    return null
  }
}
